import { Check, TriangleAlert } from "lucide-react";
import DOMPurify from "dompurify";

/**
 * Schede tipizzate delle lezioni (brief "Medicina dello sport").
 * 8 tipi: sintesi · numeri · confronto · tabella · steps · trappola · fonti · quiz.
 * Colori dal brief: verde #16A34A (accenti), navy #111827 (badge step), ambra #B45309/#FBF1E3 (trappola).
 * Il payload arriva dal DB (import admin): l'HTML inline è limitato a <b> come da schema.
 */

const GREEN = "#16A34A";
const NAVY = "#111827";
const AMBER = "#B45309";
const AMBER_BG = "#FBF1E3";

/* Involucro comune: card bianca, header uppercase con pallino verde */
const CardShell = ({ title, type, accent = GREEN, children }) => (
  <section
    className="rounded-3xl border border-[color:var(--hairline)] bg-white overflow-hidden fade-up"
    data-testid={`lesson-card-${type}`}
  >
    {title && (
      <header className="flex items-center gap-2 px-5 pt-4">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: accent }} aria-hidden="true" />
        <h3 className="text-xs font-extrabold tracking-[0.14em] uppercase">{title}</h3>
      </header>
    )}
    <div className={title ? "px-5 pb-5 pt-3" : "p-5"}>{children}</div>
  </section>
);

/* Figura di sezione: SVG inline sanificato (DOMPurify) + didascalia. Scala in larghezza. */
const SectionFigure = ({ svg, caption }) => {
  if (!svg) return null;
  const clean = DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } });
  return (
    <figure className="mt-4 mb-0.5" data-testid="lesson-card-figure">
      <div
        className="w-full overflow-hidden [&_svg]:w-full [&_svg]:h-auto [&_svg]:block text-[color:var(--ink)]"
        dangerouslySetInnerHTML={{ __html: clean }}
      />
      {caption && (
        <figcaption className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.08em] text-[color:var(--text-2)]">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

/* sintesi — bordo sinistro verde, paragrafi con <b>, + eventuale diagramma SVG di sezione */
const SchedaSintesi = ({ payload }) => (
  <CardShell title={payload.title} type="sintesi">
    <div className="border-l-4 pl-4 space-y-3" style={{ borderColor: GREEN }}>
      {(payload.paragraphs || []).map((p, i) => (
        <p
          key={i}
          className="text-sm leading-6 [&_b]:font-extrabold"
          dangerouslySetInnerHTML={{ __html: p }}
        />
      ))}
    </div>
    <SectionFigure svg={payload.svg} caption={payload.caption} />
  </CardShell>
);

/* numeri — chiave→valore in mono, highlight verde */
const SchedaNumeri = ({ payload }) => (
  <CardShell title={payload.title} type="numeri">
    <dl className="divide-y divide-[color:var(--hairline)]">
      {(payload.rows || []).map((r, i) => (
        <div key={i} className="flex items-baseline justify-between gap-4 py-2.5">
          <dt className="text-sm leading-5 shrink-0 max-w-[50%]">{r.k}</dt>
          <dd
            className={`font-mono text-sm text-right min-w-0 break-words ${r.highlight ? "font-bold" : "font-semibold"}`}
            style={r.highlight ? { color: GREEN } : undefined}
          >
            {r.v}
          </dd>
        </div>
      ))}
    </dl>
  </CardShell>
);

/* confronto — 2 colonne (col_a accento verde) con etichette di riga */
const SchedaConfronto = ({ payload }) => (
  <CardShell title={payload.title} type="confronto">
    <div className="overflow-x-auto -mx-1 px-1">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            <th className="w-1/4" aria-hidden="true" />
            <th className="text-left font-extrabold text-xs uppercase tracking-wide pb-2 pr-3" style={{ color: GREEN }}>
              {payload.col_a}
            </th>
            <th className="text-left font-extrabold text-xs uppercase tracking-wide pb-2">{payload.col_b}</th>
          </tr>
        </thead>
        <tbody className="align-top">
          {(payload.rows || []).map((r, i) => (
            <tr key={i} className="border-t border-[color:var(--hairline)]">
              <th scope="row" className="text-left font-mono text-[11px] uppercase tracking-wide text-[color:var(--text-2)] py-2.5 pr-3">
                {r.label}
              </th>
              <td className="py-2.5 pr-3 leading-5">{r.a}</td>
              <td className="py-2.5 leading-5">{r.b}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </CardShell>
);

/* tabella — headers + rows, prima colonna in evidenza */
const SchedaTabella = ({ payload }) => (
  <CardShell title={payload.title} type="tabella">
    <div className="overflow-x-auto -mx-1 px-1">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            {(payload.headers || []).map((h, i) => (
              <th key={i} className="text-left font-extrabold text-xs uppercase tracking-wide pb-2 pr-3">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="align-top">
          {(payload.rows || []).map((row, i) => (
            <tr key={i} className="border-t border-[color:var(--hairline)]">
              {row.map((cell, j) => (
                <td key={j} className={`py-2.5 pr-3 leading-5 ${j === 0 ? "font-bold" : ""}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </CardShell>
);

/* steps — elenco numerato con badge navy */
const SchedaSteps = ({ payload }) => (
  <CardShell title={payload.title} type="steps">
    <ol className="space-y-3.5">
      {(payload.steps || []).map((s, i) => (
        <li key={i} className="flex gap-3">
          <span
            className="shrink-0 w-7 h-7 rounded-full text-white font-mono text-xs font-bold flex items-center justify-center"
            style={{ background: NAVY }}
            aria-hidden="true"
          >
            {s.n ?? i + 1}
          </span>
          <div className="min-w-0 pt-0.5">
            <p className="text-sm font-extrabold leading-5">{s.label}</p>
            {s.desc && <p className="text-sm leading-5 text-[color:var(--text-2)] mt-0.5">{s.desc}</p>}
          </div>
        </li>
      ))}
    </ol>
  </CardShell>
);

/* trappola — header ambra su fondo caldo */
const SchedaTrappola = ({ payload }) => (
  <section
    className="rounded-3xl border overflow-hidden fade-up"
    style={{ background: AMBER_BG, borderColor: "rgba(180,83,9,0.25)" }}
    data-testid="lesson-card-trappola"
  >
    <header className="flex items-center gap-2 px-5 pt-4">
      <TriangleAlert className="w-4 h-4 shrink-0" style={{ color: AMBER }} aria-hidden="true" />
      <h3 className="text-xs font-extrabold tracking-[0.14em] uppercase" style={{ color: AMBER }}>
        {payload.title || "Trappola d'esame"}
      </h3>
    </header>
    <p
      className="px-5 pb-5 pt-3 text-sm leading-6 [&_b]:font-extrabold [&_b]:text-[#B45309]"
      dangerouslySetInnerHTML={{ __html: payload.body || "" }}
    />
  </section>
);

/* fonti — barra tratteggiata con chip mono */
const SchedaFonti = ({ payload }) => (
  <div
    className="rounded-2xl border border-dashed border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] px-4 py-3 fade-up"
    data-testid="lesson-card-fonti"
  >
    <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--text-2)] mb-2">Fonti</p>
    <div className="flex flex-wrap gap-1.5">
      {(payload.items || []).map((f, i) => (
        <span key={i} className="font-mono text-[11px] rounded-full border border-[color:var(--hairline)] bg-white px-2.5 py-1">
          {f}
        </span>
      ))}
    </div>
  </div>
);

/* aggiornamento — novità regolamentari: chip tag Volt + testo (Regola 2025/26 · 2026/27) */
const SchedaAggiornamento = ({ payload }) => (
  <CardShell title={payload.title || "Cosa è cambiato di recente"} type="aggiornamento">
    <ul className="space-y-3.5">
      {(payload.items || []).map((it, i) => (
        <li key={i} className="flex gap-3 items-start">
          <span
            className="shrink-0 mt-0.5 font-mono text-[10px] font-extrabold uppercase tracking-wide rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] px-2.5 py-1 whitespace-nowrap"
            data-testid="aggiornamento-tag"
          >
            {it.tag}
          </span>
          <p
            className="text-sm leading-6 [&_b]:font-extrabold"
            dangerouslySetInnerHTML={{ __html: it.text || "" }}
          />
        </li>
      ))}
    </ul>
  </CardShell>
);

/* Diagramma: schema del fuorigioco — attaccante oltre il penultimo difensore al passaggio */
const DiagrammaFuorigioco = () => (
  <svg viewBox="0 0 320 180" role="img" aria-label="Schema del fuorigioco" className="w-full h-auto rounded-2xl">
    {/* campo */}
    <rect x="0" y="0" width="320" height="180" fill="#14532D" />
    {[0, 1, 2, 3, 4].map((i) => (
      <rect key={i} x={i * 64} y="0" width="32" height="180" fill="#166534" opacity="0.5" />
    ))}
    {/* porta a destra */}
    <rect x="308" y="60" width="8" height="60" fill="#fff" opacity="0.85" />
    <line x1="304" y1="0" x2="304" y2="180" stroke="#fff" strokeOpacity="0.4" strokeWidth="1.5" />
    {/* linea del fuorigioco (penultimo difensore) */}
    <line x1="205" y1="8" x2="205" y2="172" stroke="#E7FF4B" strokeWidth="2.5" strokeDasharray="7 6" />
    <text x="205" y="18" textAnchor="middle" fontSize="9" fontFamily="monospace" fontWeight="700" fill="#E7FF4B">LINEA</text>
    {/* penultimo difensore (blu) sulla linea */}
    <circle cx="205" cy="96" r="10" fill="#2563EB" stroke="#fff" strokeWidth="1.5" />
    <text x="205" y="99" textAnchor="middle" fontSize="9" fontFamily="monospace" fontWeight="700" fill="#fff">D</text>
    {/* passatore (rosso) con palla, a sinistra */}
    <circle cx="95" cy="120" r="10" fill="#DC2626" stroke="#fff" strokeWidth="1.5" />
    <text x="95" y="123" textAnchor="middle" fontSize="9" fontFamily="monospace" fontWeight="700" fill="#fff">A1</text>
    <circle cx="108" cy="112" r="4" fill="#fff" />
    {/* attaccante (rosso) oltre la linea = posizione di fuorigioco */}
    <circle cx="258" cy="72" r="10" fill="#DC2626" stroke="#E7FF4B" strokeWidth="2.5" />
    <text x="258" y="75" textAnchor="middle" fontSize="9" fontFamily="monospace" fontWeight="700" fill="#fff">A2</text>
    {/* freccia passaggio */}
    <defs>
      <marker id="arrowFG" markerWidth="8" markerHeight="8" refX="5" refY="3" orient="auto">
        <path d="M0,0 L6,3 L0,6 Z" fill="#fff" />
      </marker>
    </defs>
    <line x1="112" y1="110" x2="246" y2="78" stroke="#fff" strokeWidth="2" strokeDasharray="5 4" markerEnd="url(#arrowFG)" />
  </svg>
);

/* Diagramma generico "campo": pitch minimale con linea di metà campo e aree */
const DiagrammaCampo = () => (
  <svg viewBox="0 0 320 180" role="img" aria-label="Schema del campo" className="w-full h-auto rounded-2xl">
    <rect x="0" y="0" width="320" height="180" fill="#14532D" />
    <rect x="6" y="6" width="308" height="168" fill="none" stroke="#fff" strokeOpacity="0.6" strokeWidth="2" />
    <line x1="160" y1="6" x2="160" y2="174" stroke="#fff" strokeOpacity="0.6" strokeWidth="2" />
    <circle cx="160" cy="90" r="26" fill="none" stroke="#fff" strokeOpacity="0.6" strokeWidth="2" />
    <rect x="6" y="50" width="44" height="80" fill="none" stroke="#fff" strokeOpacity="0.6" strokeWidth="2" />
    <rect x="270" y="50" width="44" height="80" fill="none" stroke="#fff" strokeOpacity="0.6" strokeWidth="2" />
  </svg>
);

/* diagramma — involucro con caption; sceglie il disegno in base a payload.kind */
const DiagrammaFasi = () => (
  <svg viewBox="0 0 340 300" role="img" aria-label="Le quattro fasi di gioco" className="w-full h-auto bg-white">
    <defs>
      <marker id="faseArrow" markerWidth="9" markerHeight="9" refX="6" refY="3" orient="auto">
        <path d="M0,0 L6,3 L0,6 Z" fill="#9CA3AF" />
      </marker>
    </defs>
    {/* frecce del ciclo (senso orario) */}
    <g fill="none" stroke="#9CA3AF" strokeWidth="2" markerEnd="url(#faseArrow)">
      <path d="M232,54 Q296,74 284,118" />
      <path d="M284,182 Q296,230 232,248" />
      <path d="M108,248 Q44,230 56,182" />
      <path d="M56,118 Q44,74 108,54" />
    </g>
    {/* POSSESSO (verde) */}
    <rect x="110" y="18" width="120" height="50" rx="12" fill="#16A34A" />
    <text x="170" y="40" textAnchor="middle" fontSize="13" fontWeight="800" fill="#fff">POSSESSO</text>
    <text x="170" y="56" textAnchor="middle" fontSize="9" fontFamily="monospace" fill="#DCFCE7">fase offensiva</text>
    {/* NON POSSESSO */}
    <rect x="110" y="232" width="120" height="50" rx="12" fill="#fff" stroke="#111827" strokeWidth="2" />
    <text x="170" y="254" textAnchor="middle" fontSize="13" fontWeight="800" fill="#111827">NON POSSESSO</text>
    <text x="170" y="270" textAnchor="middle" fontSize="9" fontFamily="monospace" fill="#6B7280">fase difensiva</text>
    {/* TRANS. POSITIVA (sinistra, tratteggiata verde) */}
    <rect x="2" y="125" width="116" height="50" rx="12" fill="#F0FDF4" stroke="#16A34A" strokeWidth="2" strokeDasharray="5 4" />
    <text x="60" y="147" textAnchor="middle" fontSize="10.5" fontWeight="800" fill="#166534">TRANS. POSITIVA</text>
    <text x="60" y="162" textAnchor="middle" fontSize="8" fontFamily="monospace" fill="#166534">palla riconquistata</text>
    {/* TRANS. NEGATIVA (destra, tratteggiata) */}
    <rect x="222" y="125" width="116" height="50" rx="12" fill="#F9FAFB" stroke="#9CA3AF" strokeWidth="2" strokeDasharray="5 4" />
    <text x="280" y="147" textAnchor="middle" fontSize="10.5" fontWeight="800" fill="#374151">TRANS. NEGATIVA</text>
    <text x="280" y="162" textAnchor="middle" fontSize="8" fontFamily="monospace" fill="#6B7280">palla persa</text>
  </svg>
);

const DIAGRAMS = { campo: DiagrammaCampo, "fasi-gioco": DiagrammaFasi, fuorigioco: DiagrammaFuorigioco };

const SchedaDiagramma = ({ payload }) => {
  const Diagram = DIAGRAMS[payload.kind] || DiagrammaFuorigioco;
  const boxed = payload.kind !== "fasi-gioco"; // il diagramma "fasi" ha sfondo bianco: niente bordo scuro
  return (
    <CardShell title={payload.title} type="diagramma">
      <div className={boxed ? "rounded-2xl overflow-hidden border border-[color:var(--hairline)]" : ""}>
        <Diagram />
      </div>
      {payload.caption && (
        <p className="mt-3 text-xs leading-5 text-[color:var(--text-2)] flex items-start gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest shrink-0 pt-0.5" style={{ color: GREEN }}>Schema</span>
          <span>{payload.caption}</span>
        </p>
      )}
    </CardShell>
  );
};

/* quiz — ripasso: risposta corretta evidenziata + spiegazione (non alimenta il pool simulazioni) */
const SchedaQuiz = ({ payload }) => (
  <CardShell title="Quiz di ripasso" type="quiz">
    <p className="text-sm font-bold leading-6">{payload.question}</p>
    <ul className="mt-3 space-y-2">
      {(payload.options || []).map((o) => {
        const isCorrect = o.key === payload.correct;
        return (
          <li
            key={o.key}
            data-testid={`quiz-option-${o.key}`}
            className={`flex items-start gap-2.5 rounded-2xl border px-3.5 py-2.5 text-sm leading-5 ${
              isCorrect ? "font-bold" : "border-[color:var(--hairline)]"
            }`}
            style={isCorrect ? { borderColor: GREEN, background: "rgba(22,163,74,0.07)", color: "#14532D" } : undefined}
          >
            <span className="font-mono text-xs font-bold pt-0.5 shrink-0">{o.key}</span>
            <span className="flex-1">{o.text}</span>
            {isCorrect && <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: GREEN }} strokeWidth={3} aria-label="Risposta corretta" />}
          </li>
        );
      })}
    </ul>
    {payload.explanation && (
      <div
        className="mt-3 rounded-2xl px-4 py-3 text-sm leading-6"
        style={{ background: "rgba(22,163,74,0.08)", border: `1px solid rgba(22,163,74,0.3)` }}
        data-testid="quiz-explanation"
      >
        <p className="font-mono text-[10px] tracking-widest uppercase font-bold mb-1" style={{ color: GREEN }}>
          Perché
        </p>
        {payload.explanation}
      </div>
    )}
  </CardShell>
);

const RENDERERS = {
  sintesi: SchedaSintesi,
  numeri: SchedaNumeri,
  confronto: SchedaConfronto,
  tabella: SchedaTabella,
  steps: SchedaSteps,
  trappola: SchedaTrappola,
  fonti: SchedaFonti,
  aggiornamento: SchedaAggiornamento,
  diagramma: SchedaDiagramma,
  quiz: SchedaQuiz,
};

/** Lista schede nell'ordine di import. Tipi sconosciuti vengono ignorati senza rompere la pagina. */
export const LessonCards = ({ cards = [] }) => (
  <div className="space-y-4" data-testid="lesson-cards-list">
    {[...cards]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((c) => {
        const Renderer = RENDERERS[c.type];
        return Renderer ? <Renderer key={c.id || c.order} payload={c.payload || {}} /> : null;
      })}
  </div>
);

/** Placeholder video "COMING SOON": scritta diagonale su sfondo a righe scure + badge durata. */
export const VideoComingSoon = ({ duration }) => (
  <div
    className="relative rounded-3xl overflow-hidden border border-[color:var(--hairline)] aspect-video select-none"
    style={{
      background: `repeating-linear-gradient(135deg, #111827 0px, #111827 14px, #1c2534 14px, #1c2534 28px)`,
    }}
    data-testid="lesson-video-coming-soon"
    role="img"
    aria-label={`Video in arrivo${duration ? `, durata ${duration}` : ""}`}
  >
    <div className="absolute inset-0 flex items-center justify-center">
      <span
        className="font-mono text-2xl md:text-4xl font-bold tracking-[0.35em] uppercase text-white/85 whitespace-nowrap"
        style={{ transform: "rotate(-15deg)" }}
      >
        Coming soon
      </span>
    </div>
    {duration && (
      <span
        className="absolute bottom-3 right-3 font-mono text-[11px] font-semibold text-white bg-black/60 backdrop-blur rounded-full px-2.5 py-1"
        data-testid="lesson-video-duration-badge"
      >
        {duration}
      </span>
    )}
  </div>
);
